import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, CheckCircle, Clock, DollarSign, UserPlus, 
  Phone, Mail, Home, Shield, TrendingUp, Briefcase,
  Edit2, CreditCard, Copy, Link2, ExternalLink, Trash2, UserX, Download, XSquare,
  Building2, HandshakeIcon, ArrowRight, Plus, Search, Globe, MapPin, FileText
} from 'lucide-react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get token from localStorage
const getToken = () => localStorage.getItem('token');

// Get the referrer signup link
const getReferrerLink = () => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/referrer/login`;
};

const statusColors = {
  'Received': 'bg-blue-100 text-blue-800',
  'Assigned to Agent': 'bg-purple-100 text-purple-800',
  'Re-assigned': 'bg-indigo-100 text-indigo-800',
  'Contacted': 'bg-amber-100 text-amber-800',
  'Appointment Set': 'bg-cyan-100 text-cyan-800',
  'Success': 'bg-emerald-100 text-emerald-800',
  'Did not proceed': 'bg-red-100 text-red-800',
  'Paid': 'bg-green-100 text-green-800'
};

const businessTypeIcons = {
  'Mortgage': Home,
  'Insurance': Shield,
  'Investments': TrendingUp
};

export default function ReferralLeads({ division = null }) {
  // division: null = show all (portal), 'mortgage' = mortgage division, 'insurance' = insurance division
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [mortgageAgents, setMortgageAgents] = useState([]);
  const [insuranceAgents, setInsuranceAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('referrals');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [assignData, setAssignData] = useState({ agent_id: '', agent_type: 'mortgage' });
  const [statusData, setStatusData] = useState({ status: '', notes: '' });
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');

  // Business Partners state
  const [partners, setPartners] = useState([]);
  const [partnerReferrals, setPartnerReferrals] = useState([]);
  const [partnerStats, setPartnerStats] = useState(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerForm, setPartnerForm] = useState({ name: '', company: '', category: '', phone: '', email: '', website: '', address: '', contact_person: '', commission_arrangement: '', notes: '' });
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralForm, setReferralForm] = useState({ partner_id: '', client_name: '', client_phone: '', client_email: '', referral_date: '', service_needed: '', notes: '' });
  const [showUpdateReferralModal, setShowUpdateReferralModal] = useState(false);
  const [selectedPartnerReferral, setSelectedPartnerReferral] = useState(null);
  const [updateReferralForm, setUpdateReferralForm] = useState({ status: '', notes: '', follow_up_date: '', outcome: '' });
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [referralStatusFilter, setReferralStatusFilter] = useState('all');
  const [partnerSearchQ, setPartnerSearchQ] = useState('');
  const [showPdfReportModal, setShowPdfReportModal] = useState(false);
  const [pdfFilters, setPdfFilters] = useState({ agent_id: '', start_date: '', end_date: '' });
  const [showEnablePortalModal, setShowEnablePortalModal] = useState(false);
  const [portalPartner, setPortalPartner] = useState(null);
  const [portalForm, setPortalForm] = useState({ email: '', password: '' });
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsReferral, setDocsReferral] = useState(null);
  const [referralDocs, setReferralDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docNotes, setDocNotes] = useState('');

  const partnerCategories = ['Debt Management', 'Home Insurance', 'Moving Companies', 'Home Inspectors', 'Real Estate'];
  const partnerReferralStatuses = ['Pending Confirmation', 'Contacted', 'Successful', 'Unsuccessful'];

  const isAdmin = user?.role === 'admin';
  const isPortalView = division === null;

  useEffect(() => {
    fetchData();
    if (!division) fetchPartnerData();
  }, [division]);

  const fetchPartnerData = async () => {
    const token = getToken();
    try {
      const [partnersRes, referralsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/business-partners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/business-partners/referrals/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/business-partners/referrals/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPartners(partnersRes.data);
      setPartnerReferrals(referralsRes.data);
      setPartnerStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    }
  };

  const fetchData = async () => {
    const token = getToken();
    try {
      const divisionParam = division ? `?division=${division}` : '';
      const requests = [
        axios.get(`${API}/api/referrals${divisionParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/insurance/agents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ];
      
      // Only fetch referrers in portal view (admin)
      if (!division) {
        requests.push(
          axios.get(`${API}/api/referrers/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        );
      }
      
      const [referralsRes, mortgageRes, insuranceRes, referrersRes] = await Promise.all(requests);
      setReferrals(referralsRes.data);
      setMortgageAgents(mortgageRes.data);
      setInsuranceAgents(insuranceRes.data);
      if (referrersRes) {
        setReferrers(referrersRes.data);
      }
    } catch (error) {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === referrals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(referrals.map(r => r.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    
    const token = getToken();
    try {
      const updatePromises = Array.from(selectedIds).map(id =>
        axios.put(`${API}/api/referrals/${id}/status`, 
          { status: bulkStatus, notes: '' },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(updatePromises);
      toast.success(`${selectedIds.size} referral(s) updated to "${bulkStatus}"`);
      setSelectedIds(new Set());
      setShowBulkStatusModal(false);
      setBulkStatus('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update some referrals');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    const token = getToken();
    try {
      const deletePromises = Array.from(selectedIds).map(id =>
        axios.delete(`${API}/api/referrals/${id}`, 
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedIds.size} referral(s) deleted`);
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some referrals');
    }
  };

  const handleBulkExport = () => {
    const selectedData = referrals.filter(r => selectedIds.has(r.id));
    if (selectedData.length === 0) {
      toast.error('No records selected for export');
      return;
    }
    
    const exportData = selectedData.map(r => ({
      'Referrer': r.referrer_name,
      'Lead Name': r.referral_name,
      'Lead Email': r.referral_email,
      'Lead Phone': r.referral_phone,
      'Business Type': r.business_type,
      'Status': r.status,
      'Assigned Agent': r.assigned_agent_name || '',
      'Fee Amount': r.referral_fee_amount,
      'Fee Paid': r.referral_fee_paid ? 'Yes' : 'No',
      'Created At': r.created_at
    }));
    
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `referrals_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${selectedData.length} referral(s) exported`);
  };

  const handleDeleteReferrer = async (referrerId, referrerName) => {
    if (!window.confirm(`Are you sure you want to delete referrer "${referrerName}"? This will also delete all their referrals. This action cannot be undone.`)) {
      return;
    }

    const token = getToken();
    try {
      await axios.delete(`${API}/api/referrers/${referrerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Referrer "${referrerName}" deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete referrer');
    }
  };

  // === Business Partner handlers ===
  const handleSavePartner = async () => {
    if (!partnerForm.name || !partnerForm.category) { toast.error('Name and category are required'); return; }
    const token = getToken();
    try {
      if (editingPartner) {
        await axios.put(`${API}/api/business-partners/${editingPartner.id}`, partnerForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/api/business-partners`, partnerForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Partner added');
      }
      setShowPartnerModal(false);
      setEditingPartner(null);
      setPartnerForm({ name: '', company: '', category: '', phone: '', email: '', website: '', address: '', contact_person: '', commission_arrangement: '', notes: '' });
      fetchPartnerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save partner');
    }
  };

  const handleDeletePartner = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all associated referrals?`)) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/business-partners/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Partner deleted');
      fetchPartnerData();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  const handleCreatePartnerReferral = async () => {
    if (!referralForm.partner_id || !referralForm.client_name) { toast.error('Partner and client name are required'); return; }
    const token = getToken();
    try {
      await axios.post(`${API}/api/business-partners/referrals`, referralForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referral logged');
      setShowReferralModal(false);
      setReferralForm({ partner_id: '', client_name: '', client_phone: '', client_email: '', referral_date: '', service_needed: '', notes: '' });
      fetchPartnerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create referral');
    }
  };

  const handleUpdatePartnerReferral = async () => {
    if (!selectedPartnerReferral) return;
    const token = getToken();
    try {
      await axios.put(`${API}/api/business-partners/referrals/${selectedPartnerReferral.id}`, updateReferralForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referral updated');
      setShowUpdateReferralModal(false);
      setSelectedPartnerReferral(null);
      fetchPartnerData();
    } catch (error) {
      toast.error('Failed to update referral');
    }
  };

  const handleDeletePartnerReferral = async (id) => {
    if (!window.confirm('Delete this referral record?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/business-partners/referrals/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referral deleted');
      fetchPartnerData();
    } catch (error) {
      toast.error('Failed to delete referral');
    }
  };

  const handleDownloadPdfReport = async () => {
    const token = getToken();
    const params = new URLSearchParams();
    if (pdfFilters.agent_id && pdfFilters.agent_id !== 'all_agents') {
      params.append('agent_id', pdfFilters.agent_id);
      params.append('report_type', 'individual');
    } else {
      params.append('report_type', 'summary');
    }
    if (pdfFilters.start_date) params.append('start_date', pdfFilters.start_date);
    if (pdfFilters.end_date) params.append('end_date', pdfFilters.end_date);
    try {
      const res = await axios.get(`${API}/api/business-partners/referrals/report/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_referral_report_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to generate report');
    }
    setShowPdfReportModal(false);
  };

  const openEditPartner = (partner) => {
    setEditingPartner(partner);
    setPartnerForm({ name: partner.name, company: partner.company || '', category: partner.category, phone: partner.phone || '', email: partner.email || '', website: partner.website || '', address: partner.address || '', contact_person: partner.contact_person || '', commission_arrangement: partner.commission_arrangement || '', notes: partner.notes || '' });
    setShowPartnerModal(true);
  };

  const categoryIcons = { 'Debt Management': DollarSign, 'Home Insurance': Shield, 'Moving Companies': Home, 'Home Inspectors': Search, 'Real Estate': Building2 };

  const handleEnablePortal = async () => {
    if (!portalPartner || !portalForm.email || !portalForm.password) { toast.error('Email and password are required'); return; }
    const token = getToken();
    try {
      await axios.put(`${API}/api/business-partners/${portalPartner.id}/enable-portal`, portalForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Portal access enabled for ${portalPartner.name}`);
      setShowEnablePortalModal(false);
      fetchPartnerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable portal');
    }
  };

  const openDocsModal = async (referral) => {
    setDocsReferral(referral);
    setShowDocsModal(true);
    setDocNotes('');
    const token = getToken();
    try {
      const res = await axios.get(`${API}/api/business-partners/referrals/${referral.id}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      setReferralDocs(res.data);
    } catch { setReferralDocs([]); }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docsReferral) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploadingDoc(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('notes', docNotes);
    try {
      await axios.post(`${API}/api/business-partners/referrals/${docsReferral.id}/documents`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setDocNotes('');
      const res = await axios.get(`${API}/api/business-partners/referrals/${docsReferral.id}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      setReferralDocs(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally { setUploadingDoc(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/business-partners/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Document deleted');
      setReferralDocs(prev => prev.filter(d => d.id !== docId));
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownloadDoc = async (docId, filename) => {
    const token = getToken();
    try {
      const res = await axios.get(`${API}/api/business-partners/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      let detail = 'Unknown error';
      if (err.response?.data instanceof Blob) {
        try { const text = await err.response.data.text(); const json = JSON.parse(text); detail = json.detail || text; } catch { detail = `HTTP ${err.response.status}`; }
      } else {
        detail = err.response?.data?.detail || err.message;
      }
      toast.error(`Download failed: ${detail}`);
    }
  };
  const categoryColors = { 'Debt Management': 'bg-violet-100 text-violet-800', 'Home Insurance': 'bg-blue-100 text-blue-800', 'Moving Companies': 'bg-amber-100 text-amber-800', 'Home Inspectors': 'bg-teal-100 text-teal-800', 'Real Estate': 'bg-rose-100 text-rose-800' };
  const referralStatusColors = { 'Pending Confirmation': 'bg-amber-100 text-amber-800', 'Contacted': 'bg-blue-100 text-blue-800', 'Successful': 'bg-emerald-100 text-emerald-800', 'Unsuccessful': 'bg-red-100 text-red-800' };

  const filteredPartners = partners.filter(p => (partnerFilter === 'all' || p.category === partnerFilter) && (!partnerSearchQ || p.name.toLowerCase().includes(partnerSearchQ.toLowerCase()) || (p.company || '').toLowerCase().includes(partnerSearchQ.toLowerCase())));
  const filteredPartnerReferrals = partnerReferrals.filter(r => (referralStatusFilter === 'all' || r.status === referralStatusFilter));

  const handleAssignAgent = async () => {
    if (!assignData.agent_id) {
      toast.error('Please select an agent');
      return;
    }

    const token = getToken();
    try {
      await axios.put(
        `${API}/api/referrals/${selectedReferral.id}/assign?agent_id=${assignData.agent_id}&agent_type=${assignData.agent_type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Agent assigned successfully');
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign agent');
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusData.status) {
      toast.error('Please select a status');
      return;
    }

    const token = getToken();
    try {
      await axios.put(
        `${API}/api/referrals/${selectedReferral.id}/status?status=${encodeURIComponent(statusData.status)}${statusData.notes ? `&notes=${encodeURIComponent(statusData.notes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleMarkPaid = async (referralId) => {
    if (!window.confirm('Mark this referral fee as paid ($200 via Interac)? This will notify the referrer.')) {
      return;
    }

    const token = getToken();
    try {
      await axios.put(
        `${API}/api/referrals/${referralId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Referral fee marked as paid. Referrer notified.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const stats = {
    total: referrals.length,
    received: referrals.filter(r => r.status === 'Received').length,
    inProgress: referrals.filter(r => ['Assigned to Agent', 'Contacted', 'Appointment Set'].includes(r.status)).length,
    successful: referrals.filter(r => r.status === 'Success').length,
    pendingPayment: referrals.filter(r => r.status === 'Success' && !r.referral_fee_paid).length,
    totalPaid: referrals.filter(r => r.referral_fee_paid).reduce((sum, r) => sum + r.referral_fee_amount, 0)
  };

  const copyReferralLink = () => {
    const link = getReferrerLink();
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        toast.success('Referral link copied to clipboard!');
      }).catch(() => {
        // Fallback to older method
        fallbackCopy(link);
      });
    } else {
      // Fallback for browsers without clipboard API
      fallbackCopy(link);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Referral link copied to clipboard!');
      } else {
        toast.error('Failed to copy link. Please copy manually.');
      }
    } catch (err) {
      toast.error('Failed to copy link. Please copy manually.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isPortalView ? 'All Referral Leads' : `${division === 'mortgage' ? 'Mortgage' : 'Insurance & Investment'} Referrals`}
          </h1>
          <p className="text-slate-500">
            {isPortalView 
              ? 'Manage and assign all referrals from partners' 
              : `Referrals assigned to ${division === 'mortgage' ? 'Mortgage' : 'Insurance & Investment'} agents`}
          </p>
        </div>
        
        {/* Bulk Actions - Show when items selected */}
        {selectedIds.size > 0 && isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleBulkExport}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="w-4 h-4 mr-2" /> Export ({selectedIds.size})
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBulkStatusModal(true)}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Edit2 className="w-4 h-4 mr-2" /> Update Status ({selectedIds.size})
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedIds.size})
            </Button>
            <Button
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              className="text-slate-500"
            >
              <XSquare className="w-4 h-4 mr-2" /> Clear
            </Button>
          </div>
        )}
        
        {/* Copy Referral Link Button - Only in Portal View */}
        {isPortalView && isAdmin && selectedIds.size === 0 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg border">
              <Link2 className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600 font-mono max-w-[200px] truncate">
                {getReferrerLink()}
              </span>
            </div>
            <Button 
              onClick={copyReferralLink}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.open(getReferrerLink(), '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.received}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
                <p className="text-xs text-slate-500">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.successful}</p>
                <p className="text-xs text-slate-500">Successful</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.totalPaid}</p>
                <p className="text-xs text-slate-500">Paid Out</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Referrals and Referrers (Portal View Only) */}
      {isPortalView && isAdmin ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="referrals">Referrals ({referrals.length})</TabsTrigger>
            <TabsTrigger value="referrers">Referrer Accounts ({referrers.length})</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">Business Partners ({partners.length})</TabsTrigger>
            <TabsTrigger value="partner-referrals" data-testid="tab-partner-referrals">Partner Referrals ({partnerReferrals.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals">
            {/* Referrals Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Referrals Yet</h3>
                    <p className="text-slate-500">Referrals from partners will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {isAdmin && (
                            <TableHead className="w-[40px]">
                              <Checkbox
                                checked={selectedIds.size === referrals.length && referrals.length > 0}
                                onCheckedChange={toggleSelectAll}
                              />
                            </TableHead>
                          )}
                          <TableHead>Date</TableHead>
                          <TableHead>Referrer</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Lead</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Agent</TableHead>
                          <TableHead>Fee</TableHead>
                          <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.map((referral) => {
                    const TypeIcon = businessTypeIcons[referral.business_type] || Briefcase;
                    return (
                      <TableRow key={referral.id} className={selectedIds.has(referral.id) ? 'bg-blue-50' : ''}>
                        {isAdmin && (
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.has(referral.id)}
                              onCheckedChange={() => toggleSelect(referral.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell className="text-sm text-slate-500">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{referral.referrer_name}</p>
                            <p className="text-xs text-slate-500">{referral.referrer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <TypeIcon className="w-3 h-3" />
                            {referral.business_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{referral.referral_name}</p>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="flex items-center gap-1 text-slate-600">
                              <Mail className="w-3 h-3" />
                              {referral.referral_email}
                            </p>
                            <p className="flex items-center gap-1 text-slate-600">
                              <Phone className="w-3 h-3" />
                              {referral.referral_phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[referral.status]}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {referral.assigned_agent_name || (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {referral.referral_fee_paid ? (
                            <Badge className="bg-green-100 text-green-800">Paid</Badge>
                          ) : referral.status === 'Success' ? (
                            <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {isAdmin && isPortalView && (
                              <Button
                                size="sm"
                                variant="outline"
                                title={referral.assigned_agent_id ? "Re-assign Agent" : "Assign Agent"}
                                onClick={() => {
                                  setSelectedReferral(referral);
                                  setAssignData({ 
                                    agent_id: referral.assigned_agent_id || '', 
                                    agent_type: referral.business_type === 'Mortgage' ? 'mortgage' : 'insurance'
                                  });
                                  setShowAssignModal(true);
                                }}
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedReferral(referral);
                                setStatusData({ status: referral.status, notes: referral.notes || '' });
                                setShowStatusModal(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {isAdmin && (referral.status === 'Success' || referral.status === 'Paid') && !referral.referral_fee_paid && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleMarkPaid(referral.id)}
                              >
                                <CreditCard className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
          </TabsContent>

          {/* Referrers Tab */}
          <TabsContent value="referrers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserX className="w-5 h-5" />
                  Referrer Accounts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {referrers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Referrers Yet</h3>
                    <p className="text-slate-500">Referrer accounts will appear here when they sign up</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead className="text-center">Referrals</TableHead>
                          <TableHead className="text-center">Successful</TableHead>
                          <TableHead className="text-right">Earnings</TableHead>
                          <TableHead>Joined</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {referrers.map((referrer) => (
                          <TableRow key={referrer.id}>
                            <TableCell className="font-medium">{referrer.name}</TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-slate-600">
                                <Mail className="w-3 h-3" />
                                {referrer.email}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-slate-600">
                                <Phone className="w-3 h-3" />
                                {referrer.phone}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">{referrer.referral_count}</TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-emerald-100 text-emerald-800">
                                {referrer.successful_referrals}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-bold text-emerald-600">
                              ${referrer.total_earnings}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(referrer.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteReferrer(referrer.id, referrer.name)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Partners Tab */}
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" /> Business Partners</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                      <Input placeholder="Search partners..." value={partnerSearchQ} onChange={e => setPartnerSearchQ(e.target.value)} className="pl-8 h-9 w-[200px]" data-testid="partner-search" />
                    </div>
                    <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                      <SelectTrigger className="w-[170px] h-9" data-testid="partner-category-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {partnerCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={() => { setEditingPartner(null); setPartnerForm({ name: '', company: '', category: '', phone: '', email: '', website: '', address: '', contact_person: '', commission_arrangement: '', notes: '' }); setShowPartnerModal(true); }} data-testid="add-partner-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Partner
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredPartners.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Partners Yet</h3>
                    <p className="text-slate-500 mb-4">Add business partners you refer clients to</p>
                    <Button onClick={() => { setEditingPartner(null); setPartnerForm({ name: '', company: '', category: '', phone: '', email: '', website: '', address: '', contact_person: '', commission_arrangement: '', notes: '' }); setShowPartnerModal(true); }}>
                      <Plus className="w-4 h-4 mr-1" /> Add First Partner
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredPartners.map(partner => {
                      const CatIcon = categoryIcons[partner.category] || Briefcase;
                      return (
                        <div key={partner.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`partner-card-${partner.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${categoryColors[partner.category] || 'bg-slate-100 text-slate-600'}`}>
                                <CatIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-sm">{partner.name}</h4>
                                {partner.company && <p className="text-xs text-slate-500">{partner.company}</p>}
                              </div>
                            </div>
                            <Badge className={categoryColors[partner.category] || 'bg-slate-100 text-slate-600'} variant="secondary">{partner.category}</Badge>
                          </div>
                          <div className="space-y-1 text-sm text-slate-600 mb-3">
                            {partner.contact_person && <p className="flex items-center gap-1.5"><Users className="w-3 h-3" /> {partner.contact_person}</p>}
                            {partner.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {partner.phone}</p>}
                            {partner.email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {partner.email}</p>}
                            {partner.website && <p className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> <a href={partner.website.startsWith('http') ? partner.website : `https://${partner.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate">{partner.website}</a></p>}
                            {partner.address && <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {partner.address}</p>}
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-500">{partner.referral_count || 0} referral(s)</span>
                              {partner.portal_enabled && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Portal</Badge>}
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setReferralForm({ partner_id: partner.id, client_name: '', client_phone: '', client_email: '', referral_date: new Date().toISOString().split('T')[0], service_needed: '', notes: '' }); setShowReferralModal(true); }} data-testid={`refer-client-${partner.id}`}>
                                <ArrowRight className="w-3 h-3 mr-1" /> Refer
                              </Button>
                              {!partner.portal_enabled && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-blue-600 hover:text-blue-800" onClick={() => { setPortalPartner(partner); setPortalForm({ email: partner.email || '', password: '' }); setShowEnablePortalModal(true); }} data-testid={`enable-portal-${partner.id}`}>
                                  <ExternalLink className="w-3 h-3 mr-1" /> Portal
                                </Button>
                              )}
                              {partner.email && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-600 hover:text-indigo-800" onClick={async () => {
                                  try {
                                    const token = getToken();
                                    await axios.post(`${API}/api/business-partners/${partner.id}/send-portal-invite`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success(`Portal invite sent to ${partner.email}`);
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send invite'); }
                                }} data-testid={`send-invite-${partner.id}`}>
                                  <Mail className="w-3 h-3 mr-1" /> Invite
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-7" onClick={() => openEditPartner(partner)}><Edit2 className="w-3 h-3" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-700" onClick={() => handleDeletePartner(partner.id, partner.name)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partner Referrals Tab */}
          <TabsContent value="partner-referrals">
            {/* Stats Row */}
            {partnerStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <Card><CardContent className="p-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><HandshakeIcon className="w-4 h-4 text-blue-600" /></div><div><p className="text-lg font-bold">{partnerStats.total_referrals}</p><p className="text-xs text-slate-500">Total</p></div></div></CardContent></Card>
                <Card><CardContent className="p-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><Clock className="w-4 h-4 text-amber-600" /></div><div><p className="text-lg font-bold">{partnerStats.pending_confirmation}</p><p className="text-xs text-slate-500">Pending</p></div></div></CardContent></Card>
                <Card><CardContent className="p-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center"><UserPlus className="w-4 h-4 text-blue-600" /></div><div><p className="text-lg font-bold">{partnerStats.contacted}</p><p className="text-xs text-slate-500">Contacted</p></div></div></CardContent></Card>
                <Card><CardContent className="p-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-emerald-600" /></div><div><p className="text-lg font-bold">{partnerStats.successful}</p><p className="text-xs text-slate-500">Successful</p></div></div></CardContent></Card>
                <Card><CardContent className="p-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-4 h-4 text-slate-600" /></div><div><p className="text-lg font-bold">{partnerStats.active_partners}</p><p className="text-xs text-slate-500">Partners</p></div></div></CardContent></Card>
              </div>
            )}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="flex items-center gap-2"><HandshakeIcon className="w-5 h-5" /> Client Referrals to Partners</CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={referralStatusFilter} onValueChange={setReferralStatusFilter}>
                      <SelectTrigger className="w-[150px] h-9" data-testid="referral-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {partnerReferralStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={() => { setReferralForm({ partner_id: '', client_name: '', client_phone: '', client_email: '', referral_date: new Date().toISOString().split('T')[0], service_needed: '', notes: '' }); setShowReferralModal(true); }} data-testid="log-referral-btn">
                      <Plus className="w-4 h-4 mr-1" /> Log Referral
                    </Button>
                    <Button variant="outline" onClick={() => { setPdfFilters({ agent_id: '', start_date: '', end_date: '' }); setShowPdfReportModal(true); }} data-testid="download-pdf-report-btn">
                      <Download className="w-4 h-4 mr-1" /> PDF Report
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredPartnerReferrals.length === 0 ? (
                  <div className="text-center py-12">
                    <HandshakeIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Referrals Logged</h3>
                    <p className="text-slate-500">Start tracking referrals to your business partners</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Agent</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Partner</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPartnerReferrals.map(ref => (
                          <TableRow key={ref.id} data-testid={`partner-referral-${ref.id}`}>
                            <TableCell className="text-sm text-slate-500 whitespace-nowrap">{ref.referral_date}</TableCell>
                            <TableCell className="text-sm font-medium">{ref.referrer_name || '-'}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{ref.client_name}</p>
                                {ref.client_phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{ref.client_phone}</p>}
                                {ref.client_email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{ref.client_email}</p>}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-sm">{ref.partner_name}</TableCell>
                            <TableCell><Badge className={categoryColors[ref.partner_category] || 'bg-slate-100'} variant="secondary">{ref.partner_category}</Badge></TableCell>
                            <TableCell className="text-sm text-slate-600">{ref.service_needed || '-'}</TableCell>
                            <TableCell><Badge className={referralStatusColors[ref.status] || 'bg-slate-100'}>{ref.status}</Badge></TableCell>
                            <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">{ref.notes || '-'}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="h-7" onClick={() => openDocsModal(ref)} data-testid={`docs-referral-${ref.id}`} title="Documents"><FileText className="w-3 h-3" /></Button>
                                <Button size="sm" variant="outline" className="h-7" onClick={() => { setSelectedPartnerReferral(ref); setUpdateReferralForm({ status: ref.status, notes: ref.notes || '', follow_up_date: ref.follow_up_date || '', outcome: ref.outcome || '' }); setShowUpdateReferralModal(true); }} data-testid={`edit-referral-${ref.id}`}><Edit2 className="w-3 h-3" /></Button>
                                <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-700" onClick={() => handleDeletePartnerReferral(ref.id)}><Trash2 className="w-3 h-3" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        /* Non-portal view - just show referrals table */
        <Card>
          <CardHeader>
            <CardTitle>Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No Referrals Yet</h3>
                <p className="text-slate-500">Referrals assigned to your division will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Referrer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((referral) => {
                      const TypeIcon = businessTypeIcons[referral.business_type] || Briefcase;
                      return (
                        <TableRow key={referral.id}>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(referral.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{referral.referrer_name}</p>
                              <p className="text-xs text-slate-500">{referral.referrer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              <TypeIcon className="w-3 h-3" />
                              {referral.business_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{referral.referral_name}</p>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="flex items-center gap-1 text-slate-600">
                                <Mail className="w-3 h-3" />
                                {referral.referral_email}
                              </p>
                              <p className="flex items-center gap-1 text-slate-600">
                                <Phone className="w-3 h-3" />
                                {referral.referral_phone}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[referral.status]}>
                              {referral.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {referral.assigned_agent_name || <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedReferral(referral);
                                setStatusData({ status: referral.status, notes: referral.notes || '' });
                                setShowStatusModal(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assign Agent Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedReferral?.assigned_agent_id ? 'Re-assign Agent' : 'Assign Agent to Referral'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReferral && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedReferral.referral_name}</p>
                <p className="text-sm text-slate-500">{selectedReferral.business_type}</p>
                {selectedReferral.assigned_agent_name && (
                  <p className="text-sm text-amber-600 mt-1">
                    Currently assigned to: {selectedReferral.assigned_agent_name}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Agent Type</label>
              <Select 
                value={assignData.agent_type} 
                onValueChange={(value) => setAssignData({ ...assignData, agent_type: value, agent_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mortgage">Mortgage Agent</SelectItem>
                  <SelectItem value="insurance">Insurance & Investment Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Select Agent</label>
              <Select 
                value={assignData.agent_id} 
                onValueChange={(value) => setAssignData({ ...assignData, agent_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {(assignData.agent_type === 'mortgage' ? mortgageAgents : insuranceAgents).map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleAssignAgent} className="w-full">
              {selectedReferral?.assigned_agent_id ? 'Re-assign Agent' : 'Assign Agent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Referral Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReferral && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedReferral.referral_name}</p>
                <p className="text-sm text-slate-500">Current: {selectedReferral.status}</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select 
                value={statusData.status} 
                onValueChange={(value) => setStatusData({ ...statusData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Received">Received</SelectItem>
                  <SelectItem value="Assigned to Agent">Assigned to Agent</SelectItem>
                  <SelectItem value="Re-assigned">Re-assigned</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                  <SelectItem value="Success">Success</SelectItem>
                  <SelectItem value="Did not proceed">Did not proceed</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={statusData.notes}
                onChange={(e) => setStatusData({ ...statusData, notes: e.target.value })}
                placeholder="Add notes about this referral..."
                rows={3}
              />
            </div>

            <Button onClick={handleUpdateStatus} className="w-full">
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Modal */}
      <Dialog open={showBulkStatusModal} onOpenChange={setShowBulkStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status for {selectedIds.size} Referral(s)</DialogTitle>
            <DialogDescription>
              Select a new status to apply to all selected referrals.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Received">Received</SelectItem>
                <SelectItem value="Assigned to Agent">Assigned to Agent</SelectItem>
                <SelectItem value="Re-assigned">Re-assigned</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                <SelectItem value="Success">Success</SelectItem>
                <SelectItem value="Did not proceed">Did not proceed</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={handleBulkStatusUpdate} 
              className="w-full"
              disabled={!bulkStatus}
            >
              Update {selectedIds.size} Referral(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} selected referral(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedIds.size} Referral(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Business Partner Modal */}
      <Dialog open={showPartnerModal} onOpenChange={setShowPartnerModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add Business Partner'}</DialogTitle>
            <DialogDescription>
              {editingPartner ? 'Update partner details' : 'Add a business partner you refer clients to'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label><Input value={partnerForm.name} onChange={e => setPartnerForm({ ...partnerForm, name: e.target.value })} placeholder="Partner name" data-testid="partner-name-input" /></div>
              <div><Label className="text-xs">Company</Label><Input value={partnerForm.company} onChange={e => setPartnerForm({ ...partnerForm, company: e.target.value })} placeholder="Company name" /></div>
            </div>
            <div><Label className="text-xs">Category *</Label>
              <Select value={partnerForm.category} onValueChange={v => setPartnerForm({ ...partnerForm, category: v })}>
                <SelectTrigger data-testid="partner-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{partnerCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact Person</Label><Input value={partnerForm.contact_person} onChange={e => setPartnerForm({ ...partnerForm, contact_person: e.target.value })} placeholder="Contact person" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={partnerForm.phone} onChange={e => setPartnerForm({ ...partnerForm, phone: e.target.value })} placeholder="Phone number" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input type="email" value={partnerForm.email} onChange={e => setPartnerForm({ ...partnerForm, email: e.target.value })} placeholder="Email" /></div>
              <div><Label className="text-xs">Website</Label><Input value={partnerForm.website} onChange={e => setPartnerForm({ ...partnerForm, website: e.target.value })} placeholder="Website URL" /></div>
            </div>
            <div><Label className="text-xs">Address</Label><Input value={partnerForm.address} onChange={e => setPartnerForm({ ...partnerForm, address: e.target.value })} placeholder="Business address" /></div>
            <div><Label className="text-xs">Commission/Fee Arrangement</Label><Input value={partnerForm.commission_arrangement} onChange={e => setPartnerForm({ ...partnerForm, commission_arrangement: e.target.value })} placeholder="e.g., $50 per referral, 10% commission" /></div>
            <div><Label className="text-xs">Notes</Label><Textarea value={partnerForm.notes} onChange={e => setPartnerForm({ ...partnerForm, notes: e.target.value })} placeholder="Additional notes..." rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartnerModal(false)}>Cancel</Button>
            <Button onClick={handleSavePartner} data-testid="save-partner-btn">{editingPartner ? 'Update' : 'Add Partner'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Referral to Partner Modal */}
      <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Refer Client to Partner</DialogTitle>
            <DialogDescription>Log a client referral to a business partner</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Business Partner *</Label>
              <Select value={referralForm.partner_id} onValueChange={v => setReferralForm({ ...referralForm, partner_id: v })}>
                <SelectTrigger data-testid="referral-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.category})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Client Name *</Label><Input value={referralForm.client_name} onChange={e => setReferralForm({ ...referralForm, client_name: e.target.value })} placeholder="Client full name" data-testid="referral-client-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input value={referralForm.client_phone} onChange={e => setReferralForm({ ...referralForm, client_phone: e.target.value })} placeholder="Client phone" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={referralForm.client_email} onChange={e => setReferralForm({ ...referralForm, client_email: e.target.value })} placeholder="Client email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Referral Date</Label><Input type="date" value={referralForm.referral_date} onChange={e => setReferralForm({ ...referralForm, referral_date: e.target.value })} /></div>
              <div><Label className="text-xs">Service Needed</Label><Input value={referralForm.service_needed} onChange={e => setReferralForm({ ...referralForm, service_needed: e.target.value })} placeholder="e.g., Home inspection" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={referralForm.notes} onChange={e => setReferralForm({ ...referralForm, notes: e.target.value })} placeholder="Additional notes..." rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReferralModal(false)}>Cancel</Button>
            <Button onClick={handleCreatePartnerReferral} data-testid="save-referral-btn">Log Referral</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Partner Referral Modal */}
      <Dialog open={showUpdateReferralModal} onOpenChange={setShowUpdateReferralModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Referral</DialogTitle>
            <DialogDescription>
              {selectedPartnerReferral && `${selectedPartnerReferral.client_name} → ${selectedPartnerReferral.partner_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Status</Label>
              <Select value={updateReferralForm.status} onValueChange={v => setUpdateReferralForm({ ...updateReferralForm, status: v })}>
                <SelectTrigger data-testid="update-referral-status"><SelectValue /></SelectTrigger>
                <SelectContent>{partnerReferralStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Follow-up Date</Label><Input type="date" value={updateReferralForm.follow_up_date} onChange={e => setUpdateReferralForm({ ...updateReferralForm, follow_up_date: e.target.value })} /></div>
            <div><Label className="text-xs">Outcome</Label><Input value={updateReferralForm.outcome} onChange={e => setUpdateReferralForm({ ...updateReferralForm, outcome: e.target.value })} placeholder="What was the result?" /></div>
            <div><Label className="text-xs">Notes</Label><Textarea value={updateReferralForm.notes} onChange={e => setUpdateReferralForm({ ...updateReferralForm, notes: e.target.value })} placeholder="Notes..." rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateReferralModal(false)}>Cancel</Button>
            <Button onClick={handleUpdatePartnerReferral} data-testid="update-referral-btn">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enable Portal Access Modal */}
      <Dialog open={showEnablePortalModal} onOpenChange={setShowEnablePortalModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enable Partner Portal Access</DialogTitle>
            <DialogDescription>
              {portalPartner && `Set up login credentials for ${portalPartner.name} so they can view and update referrals.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">Partner Portal URL:</p>
              <p className="font-mono text-xs">{window.location.origin}/partner/login</p>
            </div>
            <div><Label className="text-xs">Login Email *</Label><Input type="email" value={portalForm.email} onChange={e => setPortalForm({ ...portalForm, email: e.target.value })} placeholder="partner@example.com" data-testid="portal-email-input" /></div>
            <div><Label className="text-xs">Password *</Label><Input type="password" value={portalForm.password} onChange={e => setPortalForm({ ...portalForm, password: e.target.value })} placeholder="Set a password" data-testid="portal-password-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnablePortalModal(false)}>Cancel</Button>
            <Button onClick={handleEnablePortal} data-testid="enable-portal-submit-btn">Enable Portal Access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Report Modal */}
      <Dialog open={showPdfReportModal} onOpenChange={setShowPdfReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Download Partner Referral Report</DialogTitle>
            <DialogDescription>Generate a PDF report of partner referrals with optional filters.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Filter by Agent (Optional)</Label>
              <Select value={pdfFilters.agent_id} onValueChange={v => setPdfFilters({ ...pdfFilters, agent_id: v })}>
                <SelectTrigger data-testid="pdf-agent-filter"><SelectValue placeholder="All Agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_agents">All Agents</SelectItem>
                  {[...new Set(partnerReferrals.filter(r => r.referrer_id).map(r => JSON.stringify({ id: r.referrer_id, name: r.referrer_name })))].map(s => { const a = JSON.parse(s); return <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Start Date</Label><Input type="date" value={pdfFilters.start_date} onChange={e => setPdfFilters({ ...pdfFilters, start_date: e.target.value })} data-testid="pdf-start-date" /></div>
              <div><Label className="text-xs">End Date</Label><Input type="date" value={pdfFilters.end_date} onChange={e => setPdfFilters({ ...pdfFilters, end_date: e.target.value })} data-testid="pdf-end-date" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPdfReportModal(false)}>Cancel</Button>
            <Button onClick={handleDownloadPdfReport} data-testid="generate-pdf-btn"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Modal */}
      <Dialog open={showDocsModal} onOpenChange={setShowDocsModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Referral Documents</DialogTitle>
            <DialogDescription>
              {docsReferral && `${docsReferral.client_name} → ${docsReferral.partner_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Upload */}
            <div className="p-4 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <Input type="text" value={docNotes} onChange={e => setDocNotes(e.target.value)} placeholder="Note (optional)" className="flex-1 h-9 text-sm" />
                <Button size="sm" variant="default" disabled={uploadingDoc} onClick={() => document.getElementById('doc-upload-input')?.click()} data-testid="upload-doc-btn">
                  {uploadingDoc ? 'Uploading...' : 'Upload File'}
                </Button>
                <input id="doc-upload-input" type="file" className="hidden" onChange={handleUploadDoc} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv" data-testid="doc-file-input" />
              </div>
              <p className="text-xs text-slate-400 mt-2">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, TXT, CSV (max 10MB)</p>
            </div>

            {/* Document List */}
            {referralDocs.length === 0 ? (
              <p className="text-center text-slate-500 py-4 text-sm">No documents uploaded yet</p>
            ) : (
              <div className="space-y-2">
                {referralDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-slate-50" data-testid={`doc-${doc.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                        <p className="text-xs text-slate-400">{(doc.file_size / 1024).toFixed(1)} KB &middot; {doc.uploaded_by} &middot; {new Date(doc.created_at).toLocaleDateString()}</p>
                        {doc.notes && <p className="text-xs text-slate-500 italic mt-0.5">{doc.notes}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => handleDownloadDoc(doc.id, doc.original_filename)} data-testid={`download-doc-${doc.id}`}><Download className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
